import { Button } from "@admin/components/atoms/Button";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { useCompany } from "@admin/hooks/useCompany";
import {
  Licenses,
  Module,
  updateCompanyLocationSettings,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const updateLicensesSchema = z.object({
  locationId: z.string().min(1),
  shiftManagement: z.boolean(),
  foodManagement: z.boolean(),
});

type UpdateLicensesData = z.infer<typeof updateLicensesSchema>;

interface LicensesModalProps extends ModalProps {
  locationId: string;
  licenses: Licenses;
  modules: Module[];
}
export function LicensesModal({
  locationId,
  licenses,
  modules,
  ...props
}: LicensesModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { company } = useCompany();
  const { handleSubmit, setValue, register, reset } =
    useForm<UpdateLicensesData>({
      resolver: zodResolver(updateLicensesSchema),
    });
  useEffect(() => {
    if (licenses) {
      setValue(
        "foodManagement",
        !!licenses?.data.find((license) => license.moduleName === "Food"),
      );
      setValue(
        "shiftManagement",
        !!licenses?.data.find((license) => license.moduleName === "Shift"),
      );
    }
    if (locationId) {
      setValue("locationId", locationId);
    }
  }, [licenses, setValue, locationId]);

  const mutation = useMutation({
    mutationFn: updateCompanyLocationSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["licensesAndTabUsers"] });
      queryClient.invalidateQueries({ queryKey: ["locationLicenses"] });
      reset();
      props?.onClose?.();
      toast.success(t("common.The changes have been saved"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(t("common.Unable to save the changes"), {
        duration: 4000,
      });
    },
  });

  const onSubmit = (data: UpdateLicensesData) => {
    const payload = [];
    if (data.foodManagement) {
      payload.push({
        companyId: company?.companyId ?? "",
        companyLocationId: data.locationId,
        moduleId:
          modules.find((module) => module.name === "FoodManagement")?.id ?? "",
        invoiceDate: 0,
      });
    }

    if (data.shiftManagement) {
      payload.push({
        companyId: company?.companyId ?? "",
        companyLocationId: data.locationId,
        moduleId:
          modules.find((module) => module.name === "ShiftManagement")?.id ?? "",
        invoiceDate: 0,
      });
    }
    // If payload empty
    if (payload.length === 0) {
      payload.push({
        companyId: company?.companyId ?? "",
        companyLocationId: data.locationId,
        moduleId: "",
        invoiceDate: 0,
      });
    }
    mutation.mutate(payload);
  };

  return (
    <Modal
      title={t("location.Location Licenses")}
      {...props}
      actionButtons={
        <>
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              props?.onClose?.();
              reset();
            }}
          >
            {t("common.Discard")}
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            onClick={handleSubmit(onSubmit)}
          >
            {t("common.Save Changes")}
          </Button>
        </>
      }
    >
      <form className="card-body flex flex-col gap-5">
        <div className="grid grid-cols-2">
          <div className="flex flex-row gap-[10px]">
            <input
              {...register("shiftManagement")}
              className="checkbox checkbox-md"
              type="checkbox"
            ></input>
            <label className="form-label flex items-center gap-1 max-w-56 !font-normal">
              {t("navigation.Shift Management")}
            </label>
          </div>
          <div className="flex flex-row gap-[10px]">
            <input
              {...register("foodManagement")}
              className="checkbox checkbox-md"
              type="checkbox"
            ></input>
            <label className="form-label flex gap-1 max-w-56 !font-normal">
              {t("navigation.Food Management")}
            </label>
          </div>

          {/* <div className="col-span-1 flex flex-col">
            <div className="flex flex-row py-[10px] gap-[10px]">
              <input
                disabled
                className="checkbox checkbox-md"
                type="checkbox"
              />
              <label className="form-label flex gap-1 max-w-56 !font-normal">
                {t("navigation.Shift Management Pro")}
              </label>
            </div>
            <div className="flex flex-row py-[10px] gap-[10px]">
              <input
                disabled
                className="checkbox checkbox-md"
                type="checkbox"
              />
              <label className="form-label flex gap-1 max-w-56 !font-normal">
                {t("navigation.Task Management")}
              </label>
            </div>
          </div> */}
        </div>
      </form>
    </Modal>
  );
}
